import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, AlertTriangle, Search, ChevronLeft, ChevronRight, Trash2, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getApiUrl } from "@/utils/api";
import CopyButton from "@/components/admin/CopyButton";

const API = getApiUrl();
const PAGE_SIZE = 10;

/** Admin -> Error Log. Every WARNING+ backend log record — unhandled
 * exceptions AND third-party API failures (Rendy, Stripe, Resend, Rightmove,
 * Zoopla, WhatsApp, etc.) — is captured app-wide and listed here, most
 * recent first. */
export default function ErrorLogTab() {
  const [errors, setErrors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [integration, setIntegration] = useState("");
  const [integrationCounts, setIntegrationCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [detailError, setDetailError] = useState(null);

  const load = async (p = page, q = search, integ = integration) => {
    setLoading(true);
    try {
      const [{ data }, countsResp] = await Promise.all([
        axios.get(`${API}/admin/error-logs`, {
          params: { page: p, page_size: PAGE_SIZE, search: q || undefined, integration: integ || undefined },
          withCredentials: true,
        }),
        axios.get(`${API}/admin/error-logs/integration-counts`, { withCredentials: true }),
      ]);
      setErrors(data.errors || []);
      setTotal(data.total || 0);
      setIntegrationCounts(countsResp.data || {});
    } catch (e) {
      console.error("Failed to load error logs:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1, search, integration);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, integration]);

  useEffect(() => {
    load(page, search, integration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleClear = async () => {
    if (!window.confirm("Clear all logged errors? This cannot be undone.")) return;
    setClearing(true);
    try {
      await axios.delete(`${API}/admin/error-logs`, { withCredentials: true });
      setPage(1);
      await load(1, search, integration);
    } catch (e) {
      console.error("Failed to clear error logs:", e);
    } finally {
      setClearing(false);
    }
  };

  const handleDeleteOne = async (errorId, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this error log entry?")) return;
    setDeletingId(errorId);
    try {
      await axios.delete(`${API}/admin/error-logs/${errorId}`, { withCredentials: true });
      if (detailError?.error_id === errorId) setDetailError(null);
      // If we just deleted the last row on a page > 1, step back a page.
      const nextPage = errors.length === 1 && page > 1 ? page - 1 : page;
      setPage(nextPage);
      await load(nextPage, search, integration);
    } catch (e2) {
      console.error("Failed to delete error log entry:", e2);
    } finally {
      setDeletingId(null);
    }
  };

  const INTEGRATIONS = ["rightmove", "zoopla", "rendy", "whatsapp", "resend", "ideal_postcodes", "claude_ai", "land_registry", "planning_applications", "planning_appeals"];

  return (
    <div className="space-y-4" data-testid="error-log-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-xl font-semibold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" /> Log
          </h3>
          <p className="text-sm text-muted-foreground">
            Every backend error — including third-party API failures (Rendy, Stripe, Resend,
            Rightmove, Zoopla, WhatsApp) — most recent first. {total} total.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(page, search, integration)}
            disabled={loading}
            data-testid="error-log-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClear}
            disabled={clearing || total === 0}
            data-testid="error-log-clear-btn"
          >
            {clearing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Clear all
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="error-log-integration-nav">
        <button
          type="button"
          onClick={() => setIntegration("")}
          className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
            !integration ? "bg-foreground text-background border-foreground" : "bg-muted/40 border-transparent hover:bg-muted"
          }`}
          data-testid="error-log-nav-all"
        >
          All ({total})
        </button>
        {INTEGRATIONS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setIntegration(p)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border capitalize transition-colors ${
              integration === p ? "bg-foreground text-background border-foreground" : "bg-muted/40 border-transparent hover:bg-muted"
            }`}
            data-testid={`error-log-nav-${p}`}
          >
            {p.replace(/_/g, " ")} ({integrationCounts[p] || 0})
          </button>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search message, source, path, or error type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="error-log-search-input"
        />
      </div>

      <div className="border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="p-3 whitespace-nowrap">Time</th>
              <th className="p-3">Level</th>
              <th className="p-3">Source</th>
              <th className="p-3">Method</th>
              <th className="p-3">Path</th>
              <th className="p-3">Type</th>
              <th className="p-3">Message</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="p-8 text-center text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
                </td>
              </tr>
            ) : errors.length === 0 ? (
              <tr>
                <td colSpan={8} className="p-8 text-center text-muted-foreground" data-testid="error-log-empty">
                  No errors logged{search ? " matching your search" : ""}. 🎉
                </td>
              </tr>
            ) : (
              errors.map((err) => (
                <tr
                  key={err.error_id}
                  className="border-t hover:bg-muted/20 cursor-pointer"
                  data-testid={`error-log-row-${err.error_id}`}
                  onClick={() => setDetailError(err)}
                >
                  <td className="p-3 whitespace-nowrap text-muted-foreground">
                    {new Date(err.timestamp).toLocaleString()}
                  </td>
                  <td className="p-3">
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        err.level === "INFO"
                          ? "bg-blue-100 text-blue-700"
                          : err.level === "WARNING"
                            ? "bg-amber-100 text-amber-700"
                            : "bg-red-100 text-red-700"
                      }`}
                    >
                      {err.level || "ERROR"}
                    </span>
                  </td>
                  <td className="p-3 font-mono text-xs truncate max-w-[160px]" title={err.source}>
                    {err.source || "-"}
                  </td>
                  <td className="p-3">
                    {err.method ? (
                      <span className="text-xs font-mono px-1.5 py-0.5 bg-muted rounded">{err.method}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="p-3 font-mono text-xs truncate max-w-[180px]" title={err.path}>{err.path || "-"}</td>
                  <td className="p-3">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                      {err.error_type}
                    </span>
                  </td>
                  <td className="p-3 max-w-md truncate" title={err.message}>{err.message}</td>
                  <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDeleteOne(err.error_id, e)}
                      disabled={deletingId === err.error_id}
                      data-testid={`error-log-delete-btn-${err.error_id}`}
                      title="Delete this error"
                    >
                      {deletingId === err.error_id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-muted-foreground hover:text-red-600" />
                      )}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              data-testid="error-log-prev-btn"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              data-testid="error-log-next-btn"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!detailError} onOpenChange={(open) => !open && setDetailError(null)}>
        <DialogContent
          className="max-w-2xl w-[calc(100vw-1.5rem)] sm:w-full max-h-[85vh] overflow-y-auto"
          data-testid="error-log-detail-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap pr-6">
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  detailError?.level === "INFO"
                    ? "bg-blue-100 text-blue-700"
                    : detailError?.level === "WARNING"
                      ? "bg-amber-100 text-amber-700"
                      : "bg-red-100 text-red-700"
                }`}
              >
                {detailError?.level || "ERROR"}
              </span>
              <span className="break-all">{detailError?.error_type}</span>
              {detailError?.integration && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted capitalize">
                  {detailError.integration}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {detailError ? new Date(detailError.timestamp).toLocaleString() : ""}
            </DialogDescription>
          </DialogHeader>
          {detailError && (
            <div className="space-y-3 text-sm min-w-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="min-w-0">
                  <div className="text-xs uppercase text-muted-foreground mb-0.5">Source</div>
                  <div className="font-mono text-xs break-all" data-testid="error-log-detail-source">{detailError.source || "-"}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase text-muted-foreground mb-0.5">Method / Path</div>
                  <div className="font-mono text-xs break-all">
                    {detailError.method ? `${detailError.method} ` : ""}{detailError.path || "-"}
                  </div>
                </div>
              </div>
              <div className="min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <div className="text-xs uppercase text-muted-foreground">Full Message</div>
                  <CopyButton text={detailError.message} testId="error-log-copy-message" />
                </div>
                <div
                  className="whitespace-pre-wrap break-all bg-muted/40 border rounded p-3 text-sm overflow-x-auto"
                  data-testid="error-log-detail-message"
                >
                  {detailError.message}
                </div>
              </div>
              {detailError.request_body && (
                <div className="min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="text-xs uppercase text-muted-foreground">Request Body</div>
                    <CopyButton text={detailError.request_body} testId="error-log-copy-request" />
                  </div>
                  <pre
                    className="whitespace-pre-wrap break-all bg-muted/40 border rounded p-3 text-xs max-h-56 overflow-auto"
                    data-testid="error-log-detail-request-body"
                  >
                    {detailError.request_body}
                  </pre>
                </div>
              )}
              {detailError.response_body && (
                <div className="min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="text-xs uppercase text-muted-foreground">Response Body</div>
                    <CopyButton text={detailError.response_body} testId="error-log-copy-response" />
                  </div>
                  <pre
                    className="whitespace-pre-wrap break-all bg-muted/40 border rounded p-3 text-xs max-h-56 overflow-auto"
                    data-testid="error-log-detail-response-body"
                  >
                    {detailError.response_body}
                  </pre>
                </div>
              )}
              {detailError.traceback && (
                <div className="min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="text-xs uppercase text-muted-foreground">Traceback</div>
                    <CopyButton text={detailError.traceback} testId="error-log-copy-traceback" />
                  </div>
                  <pre
                    className="whitespace-pre-wrap break-all bg-slate-950 text-slate-100 rounded p-3 text-xs max-h-72 overflow-auto"
                    data-testid="error-log-detail-traceback"
                  >
                    {detailError.traceback}
                  </pre>
                </div>
              )}
              <div className="flex justify-end pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                  onClick={(e) => handleDeleteOne(detailError.error_id, e)}
                  disabled={deletingId === detailError.error_id}
                  data-testid="error-log-detail-delete-btn"
                >
                  {deletingId === detailError.error_id ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Delete this error
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
